/** New trip-type ("Filter") dialog: pick a ROOT tenant + name + REQUIRED image
 *  (+ optional description), POST multipart, then navigate to the created
 *  filter. */
import { Box, Button, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tripTypesApi from '@/api/tripTypes';
import { AppDialog } from '@/components/AppDialog';
import { ImageUploadField, type ImageFieldValue } from '@/components/ImageUploadField/ImageUploadField';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

export function TripTypeCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState<ImageFieldValue>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    resetTenant();
    setName('');
    setDescription('');
    setImage(null);
    setError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      tripTypesApi.create(
        {
          tour_operator: tenant!.id,
          name: name.trim(),
          description: description.trim() || null,
        },
        image as File,
      ),
    onSuccess: (tripType) => {
      snackbar.show('Trip type created.', 'success');
      qc.invalidateQueries({ queryKey: ['trip_types'] });
      reset();
      onClose();
      navigate(`/trip-types/${tripType.id}`);
    },
    onError: (e) => {
      // Surface a non-unique-name 400 (and any other server message) inline.
      if (e instanceof ConsoleApiError) {
        const body = e.body as Record<string, unknown> | undefined;
        const nameErr = body?.name;
        if (Array.isArray(nameErr) && typeof nameErr[0] === 'string') {
          setError(nameErr[0]);
          return;
        }
        setError(e.message);
        return;
      }
      setError('Create failed.');
    },
  });

  const canSubmit =
    tenant !== null && name.trim().length > 0 && image instanceof File && !mutation.isPending;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="New trip type"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Creating…' : 'Create trip type'}
          </Button>
        </>
      }
    >
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TenantPicker
            value={tenant} featured={pageTenants} onChange={setTenant} rootsOnly
            label="Operator" placeholder="Select root operator"
          />
          <TextField
            label="Name" size="small" fullWidth value={name} required
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 1024 }}
          />
          <TextField
            label="Description" size="small" fullWidth multiline minRows={2} value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <ImageUploadField
            label="Trip type image (required)" currentUrl={null}
            value={image} onChange={setImage} canManage
          />
          {error && <Box sx={{ color: 'error.main', fontSize: 13 }}>{error}</Box>}
        </Box>
    </AppDialog>
  );
}
